"use client";
import React from "react";
import { IconCheck, IconMinus } from "@tabler/icons-react";
import { useLocale } from "next-intl";

type FeatureRow = {
  feature: string;
  solo: boolean | string;
  team: boolean | string;
};

const tableContent = {
  en: {
    heading: "Compare plans",
    subheading: "Every plan includes the core calling stack. Team adds collaboration at scale.",
    solo: "Solo",
    team: "Team",
    rows: [
      { feature: "Credits included / month", solo: "15,000", team: "15,000 / seat" },
      { feature: "Browser-based VoIP calling", solo: true, team: true },
      { feature: "Call recording & transcription", solo: true, team: true },
      { feature: "AI-generated call summaries & outcomes", solo: true, team: true },
      { feature: "Outcome-based workflow automation", solo: true, team: true },
      { feature: "Lead discovery (Google Maps, CSV, web forms)", solo: true, team: true },
      { feature: "Smart calling lists & filters", solo: true, team: true },
      { feature: "Phone scripts during live calls", solo: true, team: true },
      { feature: "Email outreach & templates", solo: true, team: true },
      { feature: "Kanban pipeline management", solo: true, team: true },
      { feature: "Real-time notifications", solo: true, team: true },
      { feature: "Analytics dashboard", solo: "Basic", team: "Advanced" },
      { feature: "Agent leaderboards", solo: false, team: true },
      { feature: "Unlimited seats", solo: false, team: true },
      { feature: "Team & role management", solo: false, team: true },
      { feature: "Role-based permissions", solo: false, team: true },
      { feature: "API access", solo: false, team: true },
      { feature: "Priority support", solo: false, team: true },
    ] as FeatureRow[],
  },
  de: {
    heading: "Pläne vergleichen",
    subheading:
      "Jeder Plan enthält den vollständigen Calling-Stack. Team ergänzt Zusammenarbeit im großen Maßstab.",
    solo: "Solo",
    team: "Team",
    rows: [
      { feature: "Credits inklusive / Monat", solo: "15.000", team: "15.000 / Seat" },
      { feature: "Browser-basiertes VoIP-Telefonieren", solo: true, team: true },
      { feature: "Anrufaufzeichnung & Transkription", solo: true, team: true },
      { feature: "KI-generierte Zusammenfassungen & Ergebnisse", solo: true, team: true },
      { feature: "Ergebnisbasierte Workflow-Automatisierung", solo: true, team: true },
      { feature: "Lead-Erkundung (Google Maps, CSV, Webformulare)", solo: true, team: true },
      { feature: "Smarte Anruflisten & Filter", solo: true, team: true },
      { feature: "Gesprächsleitfäden während Live-Anrufen", solo: true, team: true },
      { feature: "E-Mail-Outreach & Vorlagen", solo: true, team: true },
      { feature: "Kanban-Pipeline-Management", solo: true, team: true },
      { feature: "Echtzeit-Benachrichtigungen", solo: true, team: true },
      { feature: "Analyse-Dashboard", solo: "Basis", team: "Erweitert" },
      { feature: "Agenten-Ranglisten", solo: false, team: true },
      { feature: "Unbegrenzte Seats", solo: false, team: true },
      { feature: "Team- & Rollenverwaltung", solo: false, team: true },
      { feature: "Rollenbasierte Berechtigungen", solo: false, team: true },
      { feature: "API-Zugang", solo: false, team: true },
      { feature: "Prioritäts-Support", solo: false, team: true },
    ] as FeatureRow[],
  },
};

function Cell({ value }: { value: boolean | string }) {
  if (value === true) {
    return (
      <div className="flex justify-center">
        <IconCheck className="w-5 h-5 text-green-500" />
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex justify-center">
        <IconMinus className="w-4 h-4 text-neutral-300 dark:text-neutral-600" />
      </div>
    );
  }
  return (
    <div className="flex justify-center">
      <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 rounded-full">
        {value}
      </span>
    </div>
  );
}

export function PricingTable() {
  const locale = useLocale();
  const c = locale === "de" ? tableContent.de : tableContent.en;

  return (
    <div className="mx-auto w-full max-w-3xl relative z-20 px-4 py-16">
      <h2 className="text-2xl font-bold text-center text-neutral-900 dark:text-white mb-2">
        {c.heading}
      </h2>
      <p className="text-center text-neutral-500 dark:text-neutral-400 text-sm mb-10">
        {c.subheading}
      </p>

      <div className="overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800">
        {/* Header row */}
        <div className="grid grid-cols-3 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
          <div className="px-5 py-4 text-sm font-semibold text-neutral-500 dark:text-neutral-400">
            Feature
          </div>
          <div className="px-5 py-4 text-sm font-semibold text-center text-neutral-800 dark:text-neutral-200">
            {c.solo}
          </div>
          <div className="px-5 py-4 text-sm font-semibold text-center text-white bg-slate-800 dark:bg-slate-700 rounded-tr-2xl">
            {c.team}
          </div>
        </div>

        {/* Feature rows */}
        {c.rows.map((row, i) => (
          <div
            key={row.feature}
            className={`grid grid-cols-3 border-b last:border-0 border-neutral-100 dark:border-neutral-800/60 ${
              i % 2 === 0 ? "bg-white dark:bg-black" : "bg-neutral-50/50 dark:bg-neutral-900/30"
            }`}
          >
            <div className="px-5 py-3.5 text-sm text-neutral-700 dark:text-neutral-300">
              {row.feature}
            </div>
            <div className="px-5 py-3.5">
              <Cell value={row.solo} />
            </div>
            <div className="px-5 py-3.5 bg-slate-50/50 dark:bg-slate-900/20">
              <Cell value={row.team} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
